import React from "react";
import { FormSection, FormField } from "@/components/common/FormSection";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/common/MultiSelect";
import { AddressLocationPicker } from "@/components/common/AddressLocationPicker";
import { DIAL_CODES, GENDER_OPTIONS, PATIENT_IDENTITY_TYPES, LANGUAGE_OPTIONS } from "@/lib/dialCodes";

export default function PatientFormFields({ form, setField, setEc, setForm, errors, facilities, lookups, isSaudi, editing }) {
  const fullName = [form.first_name, form.second_name, form.last_name].map((s) => (s || "").trim()).filter(Boolean).join(" ");
  const snOptions = (lookups?.special_needs || []).map((o) => ({ value: o.value, label: o.label }));
  const alOptions = (lookups?.patient_allergies || []).map((o) => ({ value: o.value, label: o.label }));
  // Nationality drives the allowed identity types (mirrors the onboarding form)
  const idTypeOptions = isSaudi
    ? PATIENT_IDENTITY_TYPES.filter((t) => t.value === "National Number")
    : (form.nationality ? PATIENT_IDENTITY_TYPES.filter((t) => t.value !== "National Number") : []);
  const idTypeLocked = !form.nationality || isSaudi;
  return (
    <>
      <FormSection title="Demographic Data" testId="patient-form-general" description="Patient identity and demographics.">
        <FormField label="MRN" hint={editing ? "System generated." : "Auto-generated on save."}>
          <Input data-testid="patient-mrn" value={editing ? form.mrn : "Auto-generated on save"} disabled />
        </FormField>
        <FormField label="First name" required error={errors.first_name}>
          <Input data-testid="patient-first-name" value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} />
        </FormField>
        <FormField label="Second name" required error={errors.second_name}>
          <Input data-testid="patient-second-name" value={form.second_name} onChange={(e) => setField("second_name", e.target.value)} />
        </FormField>
        <FormField label="Last name" required error={errors.last_name}>
          <Input data-testid="patient-last-name" value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} />
        </FormField>
        <FormField label="Full name" hint="System generated.">
          <Input data-testid="patient-full-name" value={fullName} disabled />
        </FormField>
        <FormField label="Date of birth" required error={errors.dob} hint="Must be at least 20 years; no future dates.">
          <Input data-testid="patient-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={form.dob} onChange={(e) => setField("dob", e.target.value)} />
        </FormField>
        <FormField label="Gender" required error={errors.gender}>
          <Select value={form.gender} onValueChange={(v) => setField("gender", v)}>
            <SelectTrigger data-testid="patient-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
            <SelectContent>
              {GENDER_OPTIONS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Nationality" required error={errors.nationality}>
          <Select value={form.nationality} onValueChange={(v) => setField("nationality", v)}>
            <SelectTrigger data-testid="patient-nationality"><SelectValue placeholder="Select nationality" /></SelectTrigger>
            <SelectContent>
              {(lookups?.nationality || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Identity type" required error={errors.identity_type}
          hint={!form.nationality ? "Select nationality first." : (isSaudi ? "Saudi nationals use a National Number." : "Passport, Permanent Resident or Border Number.")}>
          <Select value={form.identity_type} onValueChange={(v) => setField("identity_type", v)} disabled={idTypeLocked}>
            <SelectTrigger data-testid="patient-identity-type" className={idTypeLocked ? "opacity-70 bg-[#F3F4F6]" : ""}>
              <SelectValue placeholder={form.nationality ? "Select identity type" : "Select nationality first"} />
            </SelectTrigger>
            <SelectContent>
              {idTypeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Identity number" required error={errors.identity_number}
          hint={form.identity_type === "National Number" ? "Starts with 1, 10 digits." : form.identity_type === "Permanent Resident" ? "Starts with 2, 10 digits." : form.identity_type === "Border Number" ? "10 digits starting with 3 or 4." : form.identity_type === "Passport" ? "5–30, letters + numbers." : "Select identity type first."}>
          <Input data-testid="patient-identity-number" disabled={!form.identity_type}
            placeholder={!form.identity_type ? "Select identity type first" : ""}
            value={form.identity_number} onChange={(e) => setField("identity_number", e.target.value.trim())} />
        </FormField>
        <FormField label="Phone" required full error={errors.phone || errors.dial_code} hint="Select the country dial code first.">
          <div className="flex gap-2">
            <Select value={form.dial_code} onValueChange={(v) => setField("dial_code", v)}>
              <SelectTrigger data-testid="patient-dial-code" className="w-[150px] shrink-0"><SelectValue placeholder="Code" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {DIAL_CODES.map((d) => <SelectItem key={d.iso3} value={d.code}>{d.code} · {d.country}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="patient-phone" className="flex-1" disabled={!form.dial_code}
              placeholder={!form.dial_code ? "Select dial code first" : "Phone number"}
              value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
          </div>
        </FormField>
        <FormField label="Email" required full error={errors.email}>
          <Input data-testid="patient-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
        </FormField>
        <FormField label="Postal code" required error={errors.zip_code}>
          <Input data-testid="patient-zip" value={form.zip_code} onChange={(e) => setField("zip_code", e.target.value)} />
        </FormField>
        <FormField label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></FormField>
        <FormField label="Home address & map location" full error={errors.address}>
          <AddressLocationPicker
            value={form.location && Object.keys(form.location).length ? form.location : { line1: form.address || "", city: form.city || "" }}
            onChange={(addr) => setForm({ ...form, location: addr, address: addr.line1 || "", city: addr.city || form.city })}
            testidPrefix="patient-addr"
          />
        </FormField>
      </FormSection>

      <FormSection title="Emergency Contact" testId="patient-form-emergency" description="Primary contact in case of emergency.">
        <FormField label="First name" required error={errors.ec_first_name}>
          <Input data-testid="patient-ec-first-name" value={form.emergency_contact?.first_name || ""} onChange={(e) => setEc("first_name", e.target.value)} />
        </FormField>
        <FormField label="Last name" required error={errors.ec_last_name}>
          <Input data-testid="patient-ec-last-name" value={form.emergency_contact?.last_name || ""} onChange={(e) => setEc("last_name", e.target.value)} />
        </FormField>
        <FormField label="Phone" required full error={errors.ec_phone || errors.ec_dial_code} hint="Select the country dial code first.">
          <div className="flex gap-2">
            <Select value={form.emergency_contact?.dial_code || ""} onValueChange={(v) => setEc("dial_code", v)}>
              <SelectTrigger data-testid="patient-ec-dial-code" className="w-[150px] shrink-0"><SelectValue placeholder="Code" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {DIAL_CODES.map((d) => <SelectItem key={d.iso3} value={d.code}>{d.code} · {d.country}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="patient-ec-phone" className="flex-1" disabled={!form.emergency_contact?.dial_code}
              placeholder={!form.emergency_contact?.dial_code ? "Select dial code first" : "Phone number"}
              value={form.emergency_contact?.phone || ""} onChange={(e) => setEc("phone", e.target.value)} />
          </div>
        </FormField>
        <FormField label="Email" required full error={errors.ec_email}>
          <Input data-testid="patient-ec-email" type="email" value={form.emergency_contact?.email || ""} onChange={(e) => setEc("email", e.target.value)} />
        </FormField>
      </FormSection>

      <FormSection title="Clinical Data" testId="patient-form-clinical" description="Special needs, allergies and accommodation.">
        <FormField label="Special needs" full hint="Multi-select. Defaults to N/A if none.">
          <MultiSelect testid="patient-special-needs" options={snOptions} values={form.special_needs || []}
            onChange={(v) => setField("special_needs", v)} placeholder="Select special needs…" showPrimaryBadge={false} />
        </FormField>
        <FormField label="Allergies" full hint="Multi-select. Defaults to N/A if none.">
          <MultiSelect testid="patient-allergies" options={alOptions} values={form.allergies || []}
            onChange={(v) => setField("allergies", v)} placeholder="Select allergies…" showPrimaryBadge={false} />
        </FormField>
        <FormField label="Language" required error={errors.language}>
          <Select value={form.language} onValueChange={(v) => setField("language", v)}>
            <SelectTrigger data-testid="patient-language"><SelectValue placeholder="Select language" /></SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Requires interpreter" required error={errors.requires_interpreter}>
          <Select value={form.requires_interpreter === null || form.requires_interpreter === undefined ? "" : (form.requires_interpreter ? "yes" : "no")}
            onValueChange={(v) => setField("requires_interpreter", v === "yes")}>
            <SelectTrigger data-testid="patient-interpreter"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      </FormSection>

      <FormSection title="Assignment" testId="patient-form-assignment" description="Facility and admission information.">
        <FormField label="Facility">
          <Select value={form.facility_id} onValueChange={(v) => setForm({ ...form, facility_id: v })}>
            <SelectTrigger data-testid="patient-facility-select"><SelectValue placeholder="Select facility" /></SelectTrigger>
            <SelectContent>{facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
          </Select>
        </FormField>
        <FormField label="Admission date">
          <Input type="date" value={form.admission_date} onChange={(e) => setForm({ ...form, admission_date: e.target.value })} />
        </FormField>
        <FormField label="Primary diagnosis" full>
          <Input value={form.primary_diagnosis} onChange={(e) => setForm({ ...form, primary_diagnosis: e.target.value })} />
        </FormField>
      </FormSection>

      <FormSection title="Configuration" testId="patient-form-notes" description="Care notes and supplementary information.">
        <FormField label="Notes" full>
          <Textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </FormField>
      </FormSection>
    </>
  );
}
